/* SELETTORE DI GRUPPO RICERCABILE.
 *
 * Lo stesso elenco serve due volte: in pagina su `/gruppi`, come indice, e
 * dentro un riquadro quando si è già dentro un gruppo e si vuole cambiarlo
 * senza perdere il posto. Un solo componente per entrambi, perché due elenchi
 * che si somigliano finiscono per cercare in modo diverso.
 *
 * Si cerca per strategia, asset, anno o numero di gruppo — i quattro modi in
 * cui uno se lo ricorda. La ricerca è locale: i gruppi sono centinaia, non un
 * milione e mezzo, quindi qui filtrare in pagina NON viola la regola 6. Il
 * numero di gruppi caricati è scritto sotto il campo proprio per non far
 * credere che si stia cercando su tutto il database.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ContenitoreDati, Distintivo, IconaCerca, Modale, ScheletroLista } from '../../componenti';
import { num } from '../../lib/formato';
import { gruppiBacktest } from '../../api';
import { usaRichiesta } from '../../api/usaRichiesta';
import type { PaginaCrud, BacktestGroup } from '../../api/tipi';
import { descrizioneGruppo, testoRicercaGruppo, type GruppoInElenco } from './dati';

/** Quanti gruppi si tengono in pagina. Oltre, il campo di ricerca è l'unico
 *  modo sensato di arrivarci comunque, e l'elenco pesa senza servire. */
const MASSIMO_GRUPPI = 300;

const CAMPI_GRUPPO = [
  'id',
  'strategyName',
  'asset',
  'currency',
  'startDate',
  'endDate',
  'timeframeArray',
  'backtestNumber',
  'isRunning',
  'createdAt',
  'updatedAt',
];

/**
 * `attiva` esiste perché il dettaglio di un gruppo non ha bisogno dell'elenco
 * finché non si apre il selettore: caricarlo sempre significherebbe una
 * richiesta in più a ogni apertura di gruppo, su una schermata che si guarda
 * dal telefono, fuori casa. Quando è spento lo stato resta «caricamento» e non
 * «vuoto»: un «vuoto» al primo fotogramma direbbe «non ci sono gruppi», che è
 * un'altra cosa.
 */
export function usaGruppi(attiva = true) {
  const richiesta = usaRichiesta<PaginaCrud<BacktestGroup>>(
    () =>
      gruppiBacktest.elenco({
        fields: CAMPI_GRUPPO,
        sort: ['id,DESC'],
        limit: MASSIMO_GRUPPI,
      }),
    [],
    { attiva, eVuoto: (p) => p.data.length === 0 },
  );

  return { ...richiesta, stato: attiva ? richiesta.stato : ('caricamento' as const) };
}

/* ==========================================================================
 * L'ELENCO
 * ======================================================================== */

export function ElencoGruppi({
  gruppi,
  idAttivo,
  onScegli,
  stato,
  messaggioErrore,
  onRiprova,
}: {
  gruppi: GruppoInElenco[];
  idAttivo?: number | null;
  onScegli: (id: number) => void;
  stato: 'caricamento' | 'pieno' | 'vuoto' | 'errore';
  messaggioErrore?: string;
  onRiprova?: () => void;
}) {
  const [query, setQuery] = useState('');

  const filtrati = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return gruppi;
    /* Ogni parola deve comparire: "sem eth 2017" deve restringere, non
       allargare come farebbe un OR. */
    const parole = q.split(/\s+/);
    return gruppi.filter((g) => {
      const testo = testoRicercaGruppo(g);
      return parole.every((p) => testo.includes(p));
    });
  }, [gruppi, query]);

  return (
    <div className="sb-colonna">
      <div className="sb-gr-cerca">
        <IconaCerca />
        <input
          className="sb-gr-cerca__campo"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="es. SEM26C, ETH, 2017, 74"
          aria-label="Cerca un gruppo per strategia, asset, anno o numero"
        />
      </div>

      <div className="sb-nota">
        {stato === 'pieno'
          ? `${num(filtrati.length)} di ${num(gruppi.length)} gruppi caricati · ricerca sui gruppi in pagina, non sul database`
          : 'ricerca per strategia, asset, anno o numero di gruppo'}
      </div>

      <ContenitoreDati
        stato={stato}
        causaVuoto="Non è stato ancora lanciato nessun gruppo di backtest."
        messaggioErrore={messaggioErrore}
        onRiprova={onRiprova}
        scheletro={<ScheletroLista righe={4} />}
      >
        {filtrati.length === 0 ? (
          <div className="sb-stato">
            <div className="sb-stato__titolo">Nessun gruppo per «{query}»</div>
            <div className="sb-stato__testo">
              Cerca per nome di strategia, asset, anno o numero di gruppo.
            </div>
          </div>
        ) : (
          <div className="sb-gr-elenco">
            {filtrati.map((g) => {
              const attivo = g.id === idAttivo;
              return (
                <button
                  key={g.id}
                  type="button"
                  className={`sb-gr-voce${attivo ? ' sb-gr-voce--attiva' : ''}`}
                  aria-current={attivo ? 'true' : undefined}
                  onClick={() => onScegli(g.id)}
                >
                  <span className="sb-gr-voce__id">#{g.id}</span>
                  <span className="sb-gr-voce__corpo">
                    <span className="sb-gr-voce__nome">
                      {g.strategyName}
                      {g.isRunning && (
                        <>
                          {' '}
                          <Distintivo tono="accento">in corso</Distintivo>
                        </>
                      )}
                    </span>
                    <span className="sb-gr-voce__meta">{descrizioneGruppo(g)}</span>
                  </span>
                  <span className="sb-gr-voce__righe">
                    {g.backtestNumber == null ? '—' : `${num(g.backtestNumber)} righe`}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </ContenitoreDati>
    </div>
  );
}

/* ==========================================================================
 * IL RIQUADRO
 * ======================================================================== */

export function SelettoreGruppo({
  aperto,
  onChiudi,
  gruppi,
  idAttivo,
  stato,
  messaggioErrore,
  onRiprova,
}: {
  aperto: boolean;
  onChiudi: () => void;
  gruppi: GruppoInElenco[];
  idAttivo?: number | null;
  stato: 'caricamento' | 'pieno' | 'vuoto' | 'errore';
  messaggioErrore?: string;
  onRiprova?: () => void;
}) {
  const vaiA = useNavigate();

  return (
    <Modale
      aperta={aperto}
      onChiudi={onChiudi}
      larga
      titolo="Gruppo di backtest"
      sottotitolo="cerca per strategia, asset, anno o numero"
    >
      <ElencoGruppi
        gruppi={gruppi}
        idAttivo={idAttivo}
        stato={stato}
        messaggioErrore={messaggioErrore}
        onRiprova={onRiprova}
        onScegli={(id) => {
          onChiudi();
          vaiA(`/gruppi/${id}`);
        }}
      />
    </Modale>
  );
}
